import { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Chip,
    Dialog,
    Divider,
    Fab,
    Grid,
    IconButton,
    Paper,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import {
    ArrowBack,
    CheckCircle,
    History,
    Cancel,
    Layers,
    AccountCircle,
    Domain,
    Business,
    UnfoldLess,
    UnfoldMore,
    EditCalendar,
    CalendarMonth,
    Download,
    Close,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import IPeriodic, { EPeriodicStatus } from '../../models/periodic/periodic';
import PeriodicService from '../../services/periodic/PeriodicService';
import { getEffectiveStatus, getStatusConfig, TaskStatsOverview, TaskTimeline } from '../../components/periodics/PeriodicStatsOverview';
import { InfoRow } from '../../components/periodics/InfoRow';
import propertyGroupService from '../../services/propertyGroupService/PropertyGroupService';
import { ScheduleHistoryBox } from '../../components/periodics/ScheduleHistoryBox';
import { ITaskHistory } from '../../models/periodic/taskHistory';
import { PeriodicValidationLocationState } from '../../models/globalDashboard/periodicValidation';
import { RELATED_TASKS_LIMIT } from '../../utils/periodicUtils';
import { HistoryCard } from '../../components/periodics/HistoryCard';
import EditOnly from '../../components/permissions/EditOnly';
import { RelatedTaskCard } from '../../components/periodics/RelatedTaskCard';
import { ActionDialog } from '../../components/periodics/ActionDialog';
import { RescheduleDialog } from '../../components/periodics/RescheduleDialog';
import { IRescheduleHistory } from '../../models/periodic/rescheduleHistory';
import moment from 'moment';
import NoDataState from '../../components/periodics/NoDataState';
import { generateFriendlyFilename } from '../../utils/download/fileUtils';
import { useTranslatedTaskName } from '../../utils/taskNameTranslation';

const PeriodicReviewManager = () => {
    const { t } = useTranslation(['periodical', 'common']);
    const location = useLocation();
    const state = location.state as PeriodicValidationLocationState;

    const [history, setHistory] = useState<ITaskHistory[]>([]);
    const [relatedPeriodics, setRelatedTasks] = useState<IPeriodic[]>([]);
    const [currentStatus, setCurrentStatus] = useState<EPeriodicStatus>(EPeriodicStatus.Todo);
    const [propertyGroupName, setPropertyGroupName] = useState<string>('');

    const [isActionModalOpen, setIsActionModalOpen] = useState(false);
    const [actionStatus, setActionStatus] = useState<EPeriodicStatus | null>(null);

    const [previewData, setPreviewData] = useState<{ url: string, user?: string, date?: string } | null>(null); const [zoomLevel, setZoomLevel] = useState(1);

    const [isViewAllOpen, setIsViewAllOpen] = useState(false);
    const [fullRelatedTasks, setFullRelatedTasks] = useState<IPeriodic[]>([]);
    const [isLoadingViewAll, setIsLoadingViewAll] = useState(false);

    const [isAllExpanded, setIsAllExpanded] = useState(true);
    const [isRescheduleExpanded, setIsRescheduleExpanded] = useState(true);

    const [isOpenReschedule, setIsOpenReschedule] = useState(false);
    const [selectedDates, setSelectedDates] = useState<Date[]>([]);
    const [scheduledDates, setScheduledDates] = useState<Date[]>([]);
    const [reason, setReason] = useState('');
    const [rescheduleHistory, setRescheduleHistory] = useState<IRescheduleHistory[]>([]);
    const [currentScheduleDate, setCurrentScheduleDate] = useState<string | undefined>(state.cleaningDate.scheduleDate);

    const { building, floor, task, cleaningDate, room, profile, propertyGroupId } = state;
    const translateTaskName = useTranslatedTaskName();
    const localizedTaskName = translateTaskName(task);

    const loadData = async () => {
        try {
            const [historyData, periodic, historyReschedule, related] = await Promise.all([
                PeriodicService.GetTaskHistory(cleaningDate.id),
                PeriodicService.GetPeriodic(cleaningDate.id),
                PeriodicService.GetRescheduleHistory(cleaningDate.id),
                PeriodicService.GetRelatedTasks(room.id, cleaningDate.id, RELATED_TASKS_LIMIT),
            ]);
            setHistory(historyData);
            setCurrentStatus(periodic.status);
            setRescheduleHistory(historyReschedule);
            setRelatedTasks(related);

            setCurrentScheduleDate(periodic.scheduleDate.toString());

            if (periodic.scheduleDate) {
                const date = new Date(periodic.scheduleDate);
                setSelectedDates([date]);
                setScheduledDates([date]);
            }
        } catch (error) {
        }
    };

    useEffect(() => {
        loadData();
    }, [cleaningDate.id, room.id]);

    useEffect(() => {
        if (!propertyGroupId) return;
        propertyGroupService.GetPropertyGroup(propertyGroupId).then((propertyGroup) => {
            setPropertyGroupName(propertyGroup.name || '');
        });
    }, [propertyGroupId]);

    const onOpenAction = (status: EPeriodicStatus) => {
        setActionStatus(status);
        setIsActionModalOpen(true);
    };

    const onSubmitAction = async (comment: string, files: File[]) => {
        const formData = new FormData();
        formData.append('periodicId', cleaningDate.id.toString());
        formData.append('status', actionStatus!.toString());
        formData.append('comment', comment);
        files.forEach((f) => formData.append('images', f));

        await PeriodicService.ExecuteAction(formData);
        await loadData();
    };

    const onRescheduleSubmit = async () => {
        if (selectedDates.length === 0) return;

        const newDate = selectedDates[selectedDates.length - 1];
        const success = await PeriodicService.ReschedulePeriodic(
            cleaningDate.id,
            newDate,
            reason
        );

        if (!success) return;

        setIsOpenReschedule(false);
        setReason('');
        setSelectedDates([]);
        await loadData();


    };

    const onOpenViewAll = async () => {
        setIsViewAllOpen(true);
        setIsLoadingViewAll(true);
        try {
            const data = await PeriodicService.GetRelatedTasks(room.id, cleaningDate.id);
            setFullRelatedTasks(data);
        } finally {
            setIsLoadingViewAll(false);
        }
    };


    const onDownloadFile = async (url: string, userName?: string, actionDate?: string) => {
        try {
            const response = await fetch(url);
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = generateFriendlyFilename(url, userName, actionDate);

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
        } catch (error) {
            window.open(url, '_blank');
        }
    };


    const effectiveStatus = getEffectiveStatus(currentStatus, cleaningDate.scheduleDate);
    const statusConfig = getStatusConfig(effectiveStatus, t);

    return (
        <Box sx={{ pb: 10, maxWidth: 1100, margin: '0 auto', p: 3, bgcolor: '#f9fafb', minHeight: '100vh' }}>
            <RescheduleDialog
                open={isOpenReschedule}
                onClose={() => setIsOpenReschedule(false)}
                selectedDates={selectedDates}
                setSelectedDates={setSelectedDates}
                scheduledDates={scheduledDates}
                setScheduledDates={setScheduledDates}
                reason={reason}
                setReason={setReason}
                onSubmit={onRescheduleSubmit}
                t={t}
            />

            <Box sx={{ mb: 4, display: 'flex', flexDirection: 'column', gap: 1.5, '@media print': { display: 'none' } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, md: 2 } }}>
                        <Divider orientation="vertical" flexItem sx={{ height: 28, alignSelf: 'center', mx: 0.5 }} />
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Typography variant="h6" sx={{ fontWeight: 800, color: '#1a237e', lineHeight: 1.2 }}>
                                    {localizedTaskName}
                                </Typography>
                                <Tooltip title={<Typography variant="caption" sx={{ fontWeight: 500 }}>{t('validation.status')}</Typography>} arrow>
                                    <Chip
                                        size="small"
                                        icon={statusConfig.icon}
                                        label={statusConfig.label}
                                        sx={{
                                            bgcolor: statusConfig.bg,
                                            color: statusConfig.color,
                                            fontWeight: 800,
                                            fontSize: '0.7rem',
                                            height: 24,
                                            '& .MuiChip-icon': { fontSize: 16, color: 'inherit' },
                                        }}
                                    />
                                </Tooltip>
                            </Box>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                                {building} • {floor} • {room.name}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Box>

            <TaskTimeline history={history} startDate={cleaningDate.scheduleDate} t={t} />

            <Grid container spacing={3} alignItems="stretch">
                <Grid item xs={12} md={4}>
                    <Paper elevation={0} sx={{ height: '99.2%', width: '100%', bgcolor: '#fff', borderRadius: 4, border: '1px solid #f0f0f0', p: 3 }}>
                        <Box sx={{ position: 'sticky', top: 24, maxHeight: 'calc(100vh - 48px)', overflowY: 'auto' }}>
                            <Typography variant="h6" sx={{ fontWeight: 800, mb: 3, color: '#1a237e' }}>
                                {localizedTaskName}
                            </Typography>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mb: 4 }}>
                                <InfoRow icon={<Business />} label={t('contract')} value={propertyGroupName} />
                                <InfoRow icon={<Domain />} label={t('validation.building')} value={building} />
                                <InfoRow icon={<Layers />} label={t('validation.floor')} value={`${floor}`} />
                                <InfoRow icon={<Layers />} label={t('validation.room')} value={`${room.name}`} />
                                <InfoRow icon={<AccountCircle />} label={t('validation.profile')} value={profile} />

                                <InfoRow
                                    icon={<CalendarMonth sx={{ color: '#1a237e' }} />}
                                    label={t('periodical:validation.date')}
                                    value={currentScheduleDate ? moment(currentScheduleDate, "YYYY-MM-DD").format("LL") : "---"} />
                            </Box>

                            <Divider sx={{ mb: 3, borderColor: '#f5f5f5' }} />

                            <Box>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 0.5 }}>
                                    {t('validation.status')}
                                </Typography>
                                <Chip
                                    icon={statusConfig.icon}
                                    label={statusConfig.label}
                                    sx={{
                                        mt: 1,
                                        width: '100%',
                                        justifyContent: 'flex-start',
                                        bgcolor: statusConfig.bg,
                                        color: statusConfig.color,
                                        fontWeight: 700,
                                        fontSize: '0.9rem',
                                        height: 45,
                                        borderRadius: 2,
                                        '& .MuiChip-icon': { color: 'inherit' },
                                    }}
                                />
                            </Box>

                            <Box sx={{ mt: 3 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, mb: 1.5, display: 'block' }}>
                                    {t('button.quickActions')}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1.5, flexDirection: 'column' }}>
                                    <EditOnly>
                                        {(currentStatus === EPeriodicStatus.ToVerify ||
                                            currentStatus === EPeriodicStatus.Todo ||
                                            currentStatus === EPeriodicStatus.Refused ||
                                            currentStatus === EPeriodicStatus.Overdue) && (
                                                <Button
                                                    variant="contained"
                                                    color="success"
                                                    onClick={() => onOpenAction(EPeriodicStatus.Accepted)}
                                                    startIcon={<CheckCircle />}
                                                    sx={{ borderRadius: 2, py: 1.2, boxShadow: 'none' }}
                                                >
                                                    {t('button.validateTask')}
                                                </Button>
                                            )}

                                        {(currentStatus === EPeriodicStatus.ToVerify || currentStatus === EPeriodicStatus.Accepted) && (
                                            <Button
                                                variant="outlined"
                                                color="error"
                                                onClick={() => onOpenAction(EPeriodicStatus.Refused)}
                                                startIcon={<Cancel />}
                                                sx={{ borderRadius: 2, fontWeight: 700, mb: 1 }}
                                            >
                                                {t('button.rejectTask')}
                                            </Button>
                                        )}

                                        {(currentStatus === EPeriodicStatus.Overdue || currentStatus === EPeriodicStatus.Todo) && (
                                            <Button
                                                startIcon={<EditCalendar />}
                                                onClick={() => setIsOpenReschedule(true)}
                                                variant="outlined"
                                                color="info"
                                                sx={{ borderRadius: 2, fontWeight: 700, mb: 1 }}
                                            >
                                                {t('periodical:validation.reschedule')}
                                            </Button>
                                        )}
                                    </EditOnly>
                                </Box>
                            </Box>
                        </Box>
                    </Paper>
                </Grid>

                <Grid item xs={12} md={8}>
                    {/* Reschedule History Section */}
                    <Box sx={{ mb: 4 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>                            <Typography
                            variant="h6"
                            sx={{ fontWeight: 700, display: 'flex', flexDirection: 'column', gap: 0.5 }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CalendarMonth />
                                {t('periodical:history.scheduleTitle')}
                                <Chip
                                    label={`${rescheduleHistory.length} ${t('validation.entries')}`}
                                    size="small"
                                    sx={{ fontWeight: 700, bgcolor: '#e0e0e0' }}
                                />
                            </Box>

                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ fontWeight: 400, ml: 4 }}
                            >
                                {t('periodical:history.scheduleSubtitle')}
                            </Typography>
                        </Typography>

                            {rescheduleHistory.length > 0 && (
                                <Button
                                    size="small"
                                    onClick={() => setIsRescheduleExpanded(!isRescheduleExpanded)}
                                    startIcon={isRescheduleExpanded ? <UnfoldLess /> : <UnfoldMore />}
                                    sx={{ textTransform: 'none', fontWeight: 700 }}
                                >
                                    {isRescheduleExpanded ? t('button.collapseAll') : t('button.expandAll')}
                                </Button>
                            )}

                        </Box>

                        {rescheduleHistory.length > 0 ? (
                            <ScheduleHistoryBox history={rescheduleHistory} t={t} isExpanded={isRescheduleExpanded} />
                        ) : (
                            <NoDataState message={t('periodical:history.noRescheduleFound')} />
                        )}
                    </Box>

                    <Box>
                        {/* Validation History Section */}
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                            <Typography
                                variant="h6"
                                sx={{ fontWeight: 700, display: 'flex', flexDirection: 'column', gap: 0.5 }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <History />
                                    {t('validation.history')}
                                    <Chip
                                        label={`${history.length} ${t('validation.entries')}`}
                                        size="small"
                                        sx={{ fontWeight: 700, bgcolor: '#e0e0e0' }}
                                    />
                                </Box>

                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ fontWeight: 400, ml: 4 }}
                                >
                                    {t('periodical:history.historySubtitle')}
                                </Typography>
                            </Typography>


                            {history.length > 0 && (
                                <Button
                                    size="small"
                                    onClick={() => setIsAllExpanded(!isAllExpanded)}
                                    startIcon={isAllExpanded ? <UnfoldLess /> : <UnfoldMore />}
                                    sx={{
                                        textTransform: 'none',
                                        fontWeight: 700,
                                        color: '#5c6bc0',
                                        '&:hover': { bgcolor: 'rgba(92, 107, 192, 0.08)' },
                                    }}
                                >
                                    {isAllExpanded ? t('button.collapseAll') : t('button.expandAll')}
                                </Button>
                            )}
                        </Box>

                        {history.length > 0 ? (
                            history.map((item, index) => (
                                <HistoryCard
                                    key={item.id || index}
                                    item={item}
                                    isExpanded={isAllExpanded}
                                    t={t}
                                    onImageClick={(url) => setPreviewData({
                                        url,
                                        user: item.userName,
                                        date: item.actionDate?.toString()
                                    })}
                                    onDownload={(url) => {
                                        console.log('item:xxx', item);
                                        onDownloadFile(
                                            url,
                                            item.userName,
                                            item.actionDate?.toString()
                                        );
                                    }}
                                />))
                        ) : (
                            <NoDataState message={t('periodical:history.noHistoryFound')} />
                        )}
                    </Box>
                </Grid>
            </Grid>

            <Box sx={{ mt: 6 }}>
                <TaskStatsOverview history={history} t={t} />
            </Box>

            <Box sx={{ mt: 6 }}>
                <Paper elevation={0} sx={{ p: 3, borderRadius: 3, bgcolor: '#ffffff', border: '1px solid #e0e0e0' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box sx={{ bgcolor: '#e0f2f1', p: 1, borderRadius: 1.5, display: 'flex' }}>
                                <History sx={{ color: '#00897b' }} />
                            </Box>
                            <Typography variant="h6" sx={{ fontWeight: 800, color: '#1a237e' }}>
                                {t('validation.related')}
                            </Typography>
                        </Box>
                        <Button
                            size="small"
                            variant="text"
                            onClick={onOpenViewAll}
                            sx={{ fontWeight: 700, textTransform: 'none' }}
                            endIcon={<ArrowBack sx={{ transform: 'rotate(180deg)', fontSize: 18 }} />}
                        >
                            {t('info.more')}
                        </Button>
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {relatedPeriodics.map((rel, i) => (
                            <RelatedTaskCard key={i} task={rel} t={t} />
                        ))}
                        {relatedPeriodics.length === 0 && (
                            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                                {t('info.noTasksFound')}
                            </Typography>
                        )}
                    </Box>
                </Paper>
            </Box>

            <Dialog
                open={!!previewData}
                onClose={() => {
                    setPreviewData(null);
                    setZoomLevel(1);
                }}
                maxWidth="xl"
                PaperProps={{ sx: { bgcolor: 'transparent', boxShadow: 'none', overflow: 'hidden' } }}
            >
                <Box sx={{ position: 'relative', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>

                    <IconButton
                        onClick={() => {
                            setPreviewData(null);
                            setZoomLevel(1);
                        }}
                        sx={{
                            position: 'absolute',
                            top: 10,
                            right: 10,
                            bgcolor: 'rgba(0,0,0,0.5)',
                            color: 'white',
                            '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' },
                            zIndex: 10
                        }}
                    >
                        <Close />
                    </IconButton>
                    <img
                        src={previewData?.url || ''}
                        alt="Preview"
                        style={{
                            maxWidth: '100vw',
                            maxHeight: '85vh',
                            objectFit: 'contain',
                            transform: `scale(${zoomLevel})`,
                            transition: '0.2s',
                            cursor: zoomLevel === 1 ? 'zoom-in' : 'zoom-out'
                        }}
                        onClick={() => setZoomLevel((z) => (z === 1 ? 1.5 : 1))}
                    />

                    <Fab
                        variant="extended"
                        color="primary"
                        onClick={(e) => {
                            e.stopPropagation();
                            if (previewData) {
                                onDownloadFile(
                                    previewData.url,
                                    previewData.user,
                                    previewData.date
                                );
                            }
                        }}
                        sx={{ position: 'absolute', bottom: 20, zIndex: 10 }}
                    >
                        <Download sx={{ mr: 1 }} />
                        {t('download')}
                    </Fab>
                </Box>
            </Dialog>

            <ActionDialog
                open={isActionModalOpen}
                onClose={() => setIsActionModalOpen(false)}
                actionStatus={actionStatus}
                onSubmit={onSubmitAction}
            />

            <Dialog open={isViewAllOpen} onClose={() => setIsViewAllOpen(false)} fullWidth maxWidth="md" PaperProps={{ sx: { borderRadius: 4, maxHeight: '85vh' } }}>
                <Box sx={{ p: 3, borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>
                        {t('validation.history')} - {room.name}
                    </Typography>
                    <Button onClick={() => setIsViewAllOpen(false)} color="inherit">
                        {t('button.close')}
                    </Button>
                </Box>
                <Box sx={{ p: 3, overflowY: 'auto' }}>
                    {isLoadingViewAll ? (
                        <Typography variant="body2">{t('info.loading')}</Typography>
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {fullRelatedTasks.map((rel, i) => (
                                <RelatedTaskCard key={i} task={rel} t={t} />
                            ))}
                        </Box>
                    )}
                </Box>
            </Dialog>
        </Box>
    );
};

export default PeriodicReviewManager;